import type { Locale } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { Alert, Box, Stack, Text } from 'braid-design-system';
import { observer } from 'mobx-react';
import { useEffect, type FC } from 'react';

import DateFormat from 'src/components/common/DateFormat';
import type { PageConfig } from 'src/libs/PageConfig';
import type { ICandidateStore } from 'src/stores/JobApplicationStore/CandidateStore';
import { STATE_ERROR, STATE_LOADING } from 'src/stores/state';
import type { TrackStatusUpdateAttributes } from 'src/utils/eventsTypes';
import useBucketLabel from 'src/utils/hooks/useBucketLabel';

import translations from './.vocab';
import { ApplicationTimeline } from './ApplicationTimeline';
import { PreviousApplicationLayout } from './PreviousApplicationLayout';
import {
  ApplicationTimelineSkeleton,
  PreviousApplicationsSkeleton,
} from './PreviousApplicationsSkeleton/PreviousApplicationsSkeleton';
import { ViewJobLinkAndAttachments } from './ViewJobLinkAndAttachments';

interface PreviousApplicationsProps {
  jobId: string;
  candidateStore: ICandidateStore;
  isVerifiedId: TrackStatusUpdateAttributes['isVerifiedId'];
  locale: Locale;
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
}

const PreviousApplications: FC<PreviousApplicationsProps> = ({
  jobId,
  candidateStore,
  isVerifiedId,
  locale,
  addCustomError,
}) => {
  const { t } = useTranslations(translations);
  const { getLabelFromBucket } = useBucketLabel();

  useEffect(() => {
    candidateStore.loadCandidate({
      jobId: Number(jobId),
      applicationCorrelationId:
        candidateStore.jobApplication.applicationCorrelationId,
      candidateEmail: candidateStore.jobApplication.email,
      autoSelectFirstOne: false,
      addCustomError,
    });
  }, [jobId, candidateStore, addCustomError]);

  const { state } = candidateStore;

  if (state === STATE_ERROR) {
    return (
      <Alert tone="critical">
        <Text size="small">{t('Error loading previous applications')}</Text>
      </Alert>
    );
  }

  return (
    <Box style={{ maxWidth: 710 }}>
      <Stack space="medium">
        {state === STATE_LOADING
          ? Array.from({ length: 3 }).map((_, index) => (
              <PreviousApplicationsSkeleton key={index} />
            ))
          : candidateStore.previousApplications.map((item) => {
              const {
                id,
                candidateId,
                positionSummary,
                bucket,
                appliedDate,
                source,
                attachmentsStore: { attachmentList: attachments },
                prospectEmailStore: { emailList },
                notes: { noteList },
                loadEmailsAndNotes,
                jobCandidateId,
                seekCandidateId,
                jobApplicationId,
              } = item;

              return (
                <PreviousApplicationLayout
                  key={id}
                  positionTitle={
                    <Text weight="strong" align="left">
                      {positionSummary.title}
                    </Text>
                  }
                  bucketAndAppliedDate={
                    <Text tone="secondary" size="xsmall">
                      {`${getLabelFromBucket(bucket)}\u00A0\u00A0`}•
                      {`\u00A0\u00A0`}
                      <DateFormat date={appliedDate} locale={locale} />
                    </Text>
                  }
                  viewJobLinkAndAttachments={
                    <ViewJobLinkAndAttachments
                      prospectId={id}
                      jobId={positionSummary.id}
                      applicationCorrelationId={jobCandidateId}
                      attachments={attachments}
                      adcentreCandidateId={candidateId}
                      seekCandidateId={seekCandidateId}
                      isVerifiedId={isVerifiedId}
                      applicationId={jobApplicationId}
                    />
                  }
                  applicationTimeline={
                    item.prospectEmailStore.state === STATE_LOADING ||
                    item.notes.state === STATE_LOADING ? (
                      <ApplicationTimelineSkeleton />
                    ) : (
                      <ApplicationTimeline
                        id={id}
                        appliedDate={appliedDate}
                        source={source}
                        emailList={emailList}
                        noteList={noteList}
                        locale={locale}
                      />
                    )
                  }
                  applicationId={id}
                  onExpanded={() => loadEmailsAndNotes({ addCustomError })}
                  titleLabel={positionSummary.title}
                />
              );
            })}
      </Stack>
    </Box>
  );
};

export default observer(PreviousApplications);
